import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api/client";

/** What HR decides about signing in. Read by every signed-in tab. */
export interface SignInSettings {
  /** Minutes without a touch before a tab signs out. 0 switches it off. */
  auto_sign_out_minutes: number;
}

export const signInSettingsKey = ["auth", "sign-in-settings"] as const;

export const signInSettingsApi = {
  read: () => api.get<SignInSettings>("/auth/sign-in-settings"),
  save: (payload: SignInSettings) => api.put<SignInSettings>("/auth/sign-in-settings", payload),
};

/**
 * Fetched once a sign-in is established and again when the tab is looked
 * at after a while, so a change HR makes reaches the other tablets without
 * anybody reloading them. A tab already counting finishes on the figure it
 * had and starts again on the new one.
 */
export function useSignInSettings() {
  return useQuery({
    queryKey: signInSettingsKey,
    queryFn: signInSettingsApi.read,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useSaveSignInSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: signInSettingsApi.save,
    onSuccess: (saved) => queryClient.setQueryData(signInSettingsKey, saved),
  });
}
